<template>
    <v-dialog v-model="dialog" scrollable max-width="500" persistent>
        <template v-slot:activator="{ on }">
            <v-list-item v-if="activatorType === 'listItem'" v-on="on" @click.stop.prevent>
                <v-list-item-icon>
                    <v-icon>mdi-swap-horizontal</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="mr-5">Change role</v-list-item-title>
            </v-list-item>
            <v-btn v-else text small class="secondary--text" v-on="on">
                <v-icon left>mdi-swap-horizontal</v-icon>
                change role
            </v-btn>
        </template>
        <v-card>
            <v-card-title class="flex-nowrap align-start">
                <div>Change role</div>

                <v-spacer></v-spacer>
                <v-btn @click="dialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-text class="mt-4">
                <v-card flat class="grey lighten-4">
                    <v-card-title class="flex-nowrap align-start">
                        {{ user.full_name }}
                    </v-card-title>
                    <v-card-subtitle>
                        {{ user.email }}
                    </v-card-subtitle>
                </v-card>
                <v-radio-group v-model="selectedRole">
                    <v-card outlined class="mb-2" :class="{ 'selected-border': selectedRole === roleTypes.ORG_MEMBER }">
                        <v-radio :label="organizationRole(roleTypes.ORG_MEMBER).label" :value="roleTypes.ORG_MEMBER" class="pa-4">
                            <template v-slot:label>
                                <v-icon left :color="selectedRole === roleTypes.ORG_MEMBER ? 'primary' : undefined">
                                    {{ organizationRole(roleTypes.ORG_MEMBER).icon }}
                                </v-icon>
                                <span :class="selectedRole === roleTypes.ORG_MEMBER ? 'primary--text' : undefined">
                                    {{ organizationRole(roleTypes.ORG_MEMBER).label }}
                                </span>
                            </template>
                        </v-radio>
                    </v-card>
                    <v-card outlined class="mb-2" :class="{ 'selected-border': selectedRole === roleTypes.ORG_FACULTY }">
                        <v-radio :label="organizationRole(roleTypes.ORG_FACULTY).label" :value="roleTypes.ORG_FACULTY" class="pa-4">
                            <template v-slot:label>
                                <v-icon left :color="selectedRole === roleTypes.ORG_FACULTY ? 'primary' : undefined">
                                    {{ organizationRole(roleTypes.ORG_FACULTY).icon }}
                                </v-icon>
                                <span :class="selectedRole === roleTypes.ORG_FACULTY ? 'primary--text' : undefined">
                                    {{ organizationRole(roleTypes.ORG_FACULTY).label }}
                                </span>
                            </template>
                        </v-radio>
                    </v-card>
                    <v-card outlined :class="{ 'selected-border': selectedRole === roleTypes.ORG_MANAGER }">
                        <v-radio :label="organizationRole(roleTypes.ORG_MANAGER).label" :value="roleTypes.ORG_MANAGER" class="pa-4">
                            <template v-slot:label>
                                <v-icon left :color="selectedRole === roleTypes.ORG_MANAGER ? 'primary' : undefined">
                                    {{ organizationRole(roleTypes.ORG_MANAGER).icon }}
                                </v-icon>
                                <span :class="selectedRole === roleTypes.ORG_MANAGER ? 'primary--text' : undefined">
                                    {{ organizationRole(roleTypes.ORG_MANAGER).label }}
                                </span>
                            </template>
                        </v-radio>
                    </v-card>
                </v-radio-group>
                <v-alert color="error" class="pa-0" v-if="user.uid === userInfo.uid" text>
                    <v-card flat>
                        <v-card-text class="error--text">
                            <v-alert type="error" text>You're about to change your own role, are you sure?</v-alert>
                            <p>To confirm, please enter your name.</p>
                            <v-form v-model="valid" ref="form" @submit.prevent="valid ? saveRole() : null">
                                <v-text-field
                                    v-model="check"
                                    :rules="[rules.required, rules.userName]"
                                    prepend-icon="mdi-lock-open-variant-outline"
                                    outlined
                                    dense
                                    label="Your Name"></v-text-field>
                            </v-form>
                        </v-card-text>
                    </v-card>
                </v-alert>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <div>
                    <v-btn v-if="success" text color="success"><v-icon>mdi-check</v-icon></v-btn>
                    <v-btn v-else text color="primary" @click="saveRole()" :disabled="!valid" :loading="saving">Save</v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { enumsData } from '@/mixins/enums'
import { mapState } from 'vuex'
import { organizationRole } from '@/utils'

export default {
    name: 'OrgUserRoleDialog',
    mixins: [enumsData],
    props: {
        user: {
            type: Object,
            required: true
        },
        activatorType: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            dialog: false,
            selectedRole: '',
            saving: false,
            success: false,
            check: '',
            valid: true,
            rules: {
                required: value => !!value || 'This field is required',
                userName: value => {
                    const userName = `${this.userInfo.given_name} ${this.userInfo.family_name}`
                    return value === userName || `Please enter your name to confirm.`
                }
            }
        }
    },
    watch: {
        dialog(nextVal, prevVal) {
            if (nextVal) {
                this.selectedRole = this.user.role
            }
            if (prevVal && !nextVal) {
                this.$emit('close-menu')
            }
        }
    },
    computed: {
        ...mapState(['userInfo', 'userMetadata'])
    },
    methods: {
        organizationRole,
        async saveRole() {
            try {
                this.saving = true
                await this.$axios.post(`/orgs/${this.$route.params.oid}/assign_role_for_user`, {
                    uid: this.user.uid,
                    role_name: this.selectedRole
                })
                this.success = true
                setTimeout(() => {
                    this.$store.dispatch('orgStore/fetchOrgUsers', this.$route.params.oid)
                    this.$store.dispatch('fetchUserOrgs')
                    this.success = false
                    this.dialog = false
                }, 2000)
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: `There was a problem updating the user's role. Please try again.`,
                    snackBarTimeout: 2000,
                    snackBarIcon: 'error'
                })
            }
            this.saving = false
        }
    }
}
</script>

<style scoped>
.selected-border {
    border-color: #3381ff !important;
}
</style>
